
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { getAllUnifiedCourses } from "@/services/unifiedCourseService";
import { getAllCoursesUnified } from "@/utils/allCourses";

export interface CourseOption {
  id: string;
  title: string;
  type: string;
  description: string;
  level: string;
}

// Helper function to safely get level from course objects
const getCourseLevel = (course: any): string => {
  return course.level || course.difficulty || 'beginner';
};

// Get all available courses from database + unified system (includes all 120+ courses across 35+ categories)
export const getAllAvailableCourses = async (): Promise<CourseOption[]> => {
  try {
    // Try database first
    const { data: dbCourses, error } = await supabase
      .from('courses')
      .select('id, title, description, level, category')
      .eq('is_active', true);

    if (!error && dbCourses && dbCourses.length > 0) {
      console.log(`📚 Found ${dbCourses.length} courses from database`);
      return dbCourses.map(course => ({
        id: course.id,
        title: course.title,
        type: course.category || 'general',
        description: course.description || '',
        level: course.level || 'beginner'
      }));
    }
  } catch (error) {
    console.warn('Database fetch failed, using fallback:', error);
  }
  
  // Fallback to unified static courses
  const allCourses = getAllCoursesUnified();
  console.log(`📚 Using ${allCourses.length} courses from static fallback`);
  
  return allCourses.map(course => ({
    id: course.id,
    title: course.title,
    type: course.courseType || 'general',
    description: course.description || '',
    level: getCourseLevel(course)
  }));
};

// Enhanced course assignment function
export const assignCourseToUser = async (
  userId: string,
  courseId: string,
  assignedBy: string
): Promise<boolean> => {
  try {
    console.log('Enhanced course assignment:', { userId, courseId, assignedBy });
    
    // Use type assertion for the new function since it's not in TypeScript types yet
    const { data, error } = await (supabase as any).rpc('admin_assign_course_safe', {
      target_user_id: userId,
      course_id_param: courseId,
      assigned_by_param: assignedBy
    });
    
    if (error) {
      console.error('Error in admin_assign_course_safe:', error);
      toast.error('Failed to assign course: ' + error.message);
      return false;
    }

    console.log('Course assignment result:', data);

    // Cast the response to the expected type
    const response = data as any;

    if (response?.success) {
      console.log('Course assigned successfully');
      toast.success(response.message || "Course assigned successfully");
      return true;
    } else {
      console.error('Course assignment failed:', response?.error);
      if (response?.error?.includes('already assigned')) {
        toast.warning(response.error);
      } else {
        toast.error(response?.error || 'Failed to assign course');
      }
      return false;
    }
  } catch (error) {
    console.error('Error assigning course:', error);
    toast.error('Failed to assign course: ' + (error as any)?.message);
    return false;
  }
};

// Enhanced bulk course assignment with improved error handling
export const bulkAssignCourses = async (
  userIds: string[],
  courseIds: string[],
  assignedBy: string
): Promise<boolean> => {
  try {
    console.log('Enhanced bulk course assignment:', { 
      userCount: userIds.length, 
      courseCount: courseIds.length, 
      assignedBy,
      totalAssignments: userIds.length * courseIds.length
    });
    
    // Use type assertion for the new function since it's not in TypeScript types yet
    const { data, error } = await (supabase as any).rpc('admin_bulk_assign_courses', {
      user_ids: userIds,
      course_ids: courseIds,
      assigned_by_param: assignedBy
    });
    
    if (error) {
      console.error('Error in admin_bulk_assign_courses:', error);
      toast.error('Failed to bulk assign courses: ' + error.message);
      return false;
    }

    console.log('Bulk assignment result:', data);

    const response = data as any;

    if (response?.success) {
      const successCount = response.success_count || 0;
      const errorCount = response.error_count || 0;
      const totalAttempted = userIds.length * courseIds.length;
      
      console.log('Assignment summary:', {
        successCount,
        errorCount,
        totalAttempted,
        errors: response.errors
      });

      // Show detailed success message
      if (errorCount === 0) {
        toast.success(`Successfully assigned all ${successCount} courses`);
      } else {
        toast.warning(`Assigned ${successCount} courses successfully, ${errorCount} failed`);
        
        // Log specific errors for debugging
        if (response.errors && response.errors.length > 0) {
          console.warn('Assignment errors:', response.errors);
        }
      }
      
      return true;
    } else {
      console.error('Bulk assignment failed:', response?.error);
      toast.error(response?.error || 'Failed to assign courses');
      return false;
    }
  } catch (error) {
    console.error('Error in bulk assignment:', error);
    toast.error('Failed to assign courses: ' + (error as any)?.message);
    return false;
  }
};

// Get user's assigned courses
export const getUserAssignedCourses = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('course_assignments')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active');

    if (error) {
      console.error('Error fetching user assignments:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserAssignedCourses:', error);
    return [];
  }
};
